import { useEffect, useState } from "react";
import { investors as investorsApi, type Investor } from "../../api";
import { useAuth } from "../../auth";
import { Link, useRouter } from "../../router";
import { InvestorCombobox } from "../quantfolioEmbed/InvestorCombobox";
import { advisoryApi, isUnavailable, type FlowOption, type OwnerMapping } from "./advisoryApi";
import { PoweredBy } from "./ui";
import "./advisory.css";

function preselectedInvestor(): number | "" {
  if (typeof window === "undefined") return "";
  const n = Number(new URLSearchParams(window.location.search).get("investor"));
  return Number.isInteger(n) && n > 0 ? n : "";
}

/**
 * Start an advice (§4.2): pick the client with the searchable investor picker,
 * the flow (defaults to Investment Advice) and the owning advisor (defaults to
 * me). The server links the client, opens the advice and returns the session.
 */
export function NewAdvice() {
  const { user } = useAuth();
  const { navigate } = useRouter();
  const [investors, setInvestors] = useState<Investor[] | null>(null);
  const [flows, setFlows] = useState<FlowOption[] | null>(null);
  const [owners, setOwners] = useState<OwnerMapping[] | null>(null);
  const [investorId, setInvestorId] = useState<number | "">(preselectedInvestor);
  const [flowId, setFlowId] = useState("");
  const [ownerId, setOwnerId] = useState<number | "">(user?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flowsDown, setFlowsDown] = useState(false);

  useEffect(() => {
    investorsApi
      .list()
      .then((d) => setInvestors(d.investors))
      .catch((e) => setErr((e as Error).message));
    advisoryApi
      .flows()
      .then((d) => {
        setFlows(d.flows);
        const def = d.flows.find((f) => f.isDefault) ?? d.flows[0];
        if (def) setFlowId(def.id);
      })
      .catch((e) => {
        setFlows([]);
        if (isUnavailable(e)) setFlowsDown(true);
        else setErr((e as Error).message);
      });
    // Owner list is a nicety: if it fails the advice is owned by me.
    advisoryApi
      .owners()
      .then((d) => setOwners(d.mappings))
      .catch(() => setOwners([]));
  }, []);

  useEffect(() => {
    if (ownerId === "" && user) setOwnerId(user.id);
  }, [user, ownerId]);

  async function start() {
    if (!investorId || !flowId || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const { session } = await advisoryApi.create({
        investorId: Number(investorId),
        flowId,
        ...(ownerId !== "" ? { ownerUserId: Number(ownerId) } : {}),
      });
      navigate(`/app/advisory/${session.id}`);
    } catch (e) {
      setErr(
        isUnavailable(e)
          ? "The advice engine is not reachable right now, so the advice could not be opened. Try again in a moment."
          : (e as Error).message,
      );
      setBusy(false);
    }
  }

  const selectedOwner = owners?.find((o) => o.user_id === ownerId);

  return (
    <div className="page adv-page">
      <header className="page-head">
        <div>
          <Link to="/app/advisory" className="back-link">
            ← Advice desk
          </Link>
          <h1>New advice</h1>
        </div>
      </header>
      <p className="muted">
        Choose the client and the kind of advice. We link the client, set up the advice with its
        first goal, and open the workbench.
      </p>

      {err && <div className="banner-error">{err}</div>}

      <div className="card-form">
        <div className="field">
          <span>Client *</span>
          <InvestorCombobox investors={investors} value={investorId} onChange={setInvestorId} />
        </div>

        <label className="field">
          <span>Flow *</span>
          <select value={flowId} onChange={(e) => setFlowId(e.target.value)} disabled={!flows || flows.length === 0}>
            {!flows && <option value="">Loading flows…</option>}
            {flows && flows.length === 0 && <option value="">No flows available</option>}
            {(flows ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
          {flowsDown && (
            <span className="field-hint">The advice engine is not reachable, so flows can't be listed right now.</span>
          )}
        </label>

        <label className="field">
          <span>Advisor (owner)</span>
          <select
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value === "" ? "" : Number(e.target.value))}
          >
            {(!owners || owners.length === 0) && user && <option value={user.id}>{user.username} (me)</option>}
            {(owners ?? []).map((o) => (
              <option key={o.user_id} value={o.user_id}>
                {o.username}
                {o.user_id === user?.id ? " (me)" : ""}
              </option>
            ))}
          </select>
          {selectedOwner?.qp_email && (
            <span className="field-hint">Recorded as owner {selectedOwner.qp_email}.</span>
          )}
        </label>

        <div className="form-actions">
          <Link to="/app/advisory" className="btn btn-ghost adv-btn">
            Cancel
          </Link>
          <button
            type="button"
            className="btn btn-primary adv-btn"
            onClick={start}
            disabled={!investorId || !flowId || busy}
          >
            {busy ? "Opening the advice…" : "Start advice"}
          </button>
        </div>
      </div>
      <PoweredBy />
    </div>
  );
}
